const DEVICE_ID_KEY = 'bige_device_id';

// Fallback when localStorage is unavailable (private mode, blocked storage).
// Module-scoped so the id stays stable for the lifetime of the page.
let memoryDeviceId: string | null = null;

const newId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // randomUUID unavailable (insecure context) — fall through.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Stable identifier for this browser/device. Shared by every tab of the same
 * browser profile, so opening a second tab never counts as a second session.
 */
export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;

    const id = newId();
    localStorage.setItem(DEVICE_ID_KEY, id);

    // Some privacy configurations accept the write and drop it. That would hand
    // this browser a new identity on every page load, so say so loudly rather
    // than letting the session logic behave inexplicably.
    if (localStorage.getItem(DEVICE_ID_KEY) !== id) {
      console.warn(
        '[SingleSession] device id did not persist — browser storage is being cleared or blocked.',
      );
    }
    return id;
  } catch {
    console.warn('[SingleSession] localStorage unavailable; device id lasts only for this page.');
    if (!memoryDeviceId) memoryDeviceId = newId();
    return memoryDeviceId;
  }
}

/** Human-readable device description, e.g. "Chrome on macOS". Best-effort. */
export function getDeviceLabel(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (!ua) return 'Unknown device';

  // Brave ships a Chrome user-agent verbatim, so the only way to tell them apart
  // is the navigator.brave hook it injects. Without this both report "Chrome",
  // which makes the message on the displaced device misleading.
  const isBrave = typeof navigator !== 'undefined' && 'brave' in navigator;

  const browser =
    isBrave ? 'Brave' :
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\//.test(ua) ? 'Opera' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' :
    'Browser';

  const os =
    /iPhone|iPad|iPod/.test(ua) ? 'iOS' :
    /Android/.test(ua) ? 'Android' :
    /Mac OS X/.test(ua) ? 'macOS' :
    /Windows/.test(ua) ? 'Windows' :
    /Linux/.test(ua) ? 'Linux' :
    'Unknown OS';

  return `${browser} on ${os}`;
}
